import type { ApiError, ApiResponse } from "@/types";
import { env } from "@services/config/env";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string>;
  signal?: AbortSignal;
}

type RequestInterceptor = (
  config: RequestInit & { url: string },
) => RequestInit & { url: string };
type ResponseInterceptor = (response: Response) => Response | Promise<Response>;

/**
 * Centralized HTTP client.
 *
 * Features:
 * - Automatic Authorization header injection.
 * - Request/response interceptors.
 * - Consistent error handling.
 * - Normalized ApiResponse envelopes.
 */
class HttpClient {
  private baseUrl: string;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private tokenGetter: (() => string | null) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /** Register a function that supplies the auth token. */
  setTokenGetter(getter: () => string | null): void {
    this.tokenGetter = getter;
  }

  /** Add a request interceptor. */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /** Add a response interceptor. */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  // ───────── Public HTTP verbs ─────────

  async get<T>(
    path: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("GET", path, undefined, options);
  }

  async post<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("POST", path, body, options);
  }

  async put<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", path, body, options);
  }

  async patch<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", path, body, options);
  }

  async delete<T>(
    path: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", path, undefined, options);
  }

  // ───────── Internal ─────────

  private buildUrl(path: string, params?: Record<string, string>): string {
    const isAbsoluteUrl = /^https?:\/\//i.test(path);
    const normalizedBaseUrl = this.baseUrl.endsWith("/")
      ? this.baseUrl
      : `${this.baseUrl}/`;
    const normalizedPath = path.replace(/^\/+/, "");
    const url = isAbsoluteUrl
      ? new URL(path)
      : new URL(normalizedPath, normalizedBaseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
    return url.toString();
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(path, options?.params);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options?.headers,
    };

    // Auto-inject token
    const token = this.tokenGetter?.();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // WAF token required by AWS ALB on every request
    if (env.apiToken) {
      headers["X-Api-Token"] = env.apiToken;
    }

    let config: RequestInit & { url: string } = {
      url,
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    };

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      config = interceptor(config);
    }

    try {
      let response = await fetch(config.url, {
        method: config.method,
        headers: config.headers,
        body: config.body,
        signal: config.signal,
      });

      // Apply response interceptors
      for (const interceptor of this.responseInterceptors) {
        response = await interceptor(response);
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const apiError: ApiError = {
          status: response.status,
          message:
            ((errorBody as Record<string, unknown>).message as string) ||
            response.statusText,
          code: (errorBody as Record<string, unknown>).code as
            | string
            | undefined,
        };
        throw apiError;
      }

      const data: ApiResponse<T> = await response.json();
      return data;
    } catch (error) {
      if (this.isApiError(error)) {
        throw error;
      }

      // Network or unexpected error
      const apiError: ApiError = {
        status: 0,
        message:
          error instanceof Error ? error.message : "Unknown network error",
        code: "NETWORK_ERROR",
      };
      throw apiError;
    }
  }

  private isApiError(error: unknown): error is ApiError {
    return (
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      "message" in error
    );
  }
}

/** Singleton HTTP client instance. */
export const httpClient = new HttpClient(env.apiBaseUrl);
